import {
  type Client,
  type FragmentOf,
  graphql,
  prefetch,
  type ResultOf,
  readFragment,
} from "@opensea/graphql"
import { create } from "zustand"
import { mutative } from "zustand-mutative"
import { MintScheduleFragment } from "@/components/collection/drops/components/MintSchedule/MintSchedule.fragment"
import type { DropStatus } from "@/components/collection/drops/constants"
import {
  findActiveDropStageIndex,
  findActiveDropStageIndexFragment,
  getDropStatus,
  getDropStatusFragment,
} from "@/components/collection/drops/utils"
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment"
import { UsdPriceFragment } from "@/components/price/UsdPrice.fragment"

export const dropStoreDropFragment = graphql(
  `
    fragment dropStore_drop on Drop {
      __typename
      ... on Erc721SeaDropV1 {
        maxSupply
        totalSupply
      }
      ... on Erc1155SeaDropV2 {
        tokenSupply {
          tokenId
          totalSupply
          maxSupply
        }
        tokenMintDetails {
          tokenId
          groupName
          groupValue
        }
      }
      stages {
        label
        stageType
        stageIndex
        startTime
        endTime
        maxTotalMintableByWallet
        price {
          ...TokenPrice
          ...UsdPrice
          usd
          token {
            unit
            symbol
            contractAddress
            chain {
              identifier
            }
          }
        }
        ... on Erc1155SeaDropV2Stage {
          fromTokenId
          toTokenId
          maxTotalMintableByWalletPerToken
        }
        ...MintSchedule
      }
    }
  `,
  [MintScheduleFragment, TokenPriceFragment, UsdPriceFragment],
)

export const dropStoreCollectionFragment = graphql(
  `
    fragment dropStore_collection on Collection {
      slug
      name
      ...findActiveDropStageIndex
      ...getDropStatus
    }
  `,
  [findActiveDropStageIndexFragment, getDropStatusFragment],
)

export const dropEligibilityQuery = graphql(
  `
    query DropEligibilityQuery($collectionSlug: String!, $address: Address!) {
      dropBySlug(slug: $collectionSlug) {
        __typename
        ... on Erc721SeaDropV1 {
          minterQuantityMinted(minter: $address)
        }
        stages {
          stageType
          stageIndex
          isEligible
          maxTotalMintableByWallet
          eligibleMaxTotalMintableByWallet
          eligiblePrice {
            ...TokenPrice
            ...UsdPrice
            usd
            token {
              unit
              symbol
              contractAddress
              chain {
                identifier
              }
            }
          }
          ... on Erc1155SeaDropV2Stage {
            fromTokenId
            toTokenId
            maxTotalMintableByWalletPerToken
            eligibleMaxTotalMintableByWalletPerToken
          }
        }
      }
    }
  `,
  [TokenPriceFragment, UsdPriceFragment],
)

export const prefetchDropEligibilityQuery = (
  collectionSlug: string,
  client: Client,
  address: string,
) => {
  prefetch(client, dropEligibilityQuery, {
    address,
    collectionSlug,
  })
}

type Price = FragmentOf<typeof TokenPriceFragment> &
  FragmentOf<typeof UsdPriceFragment> & {
    usd: number
    token: {
      unit: number
      symbol: string
      contractAddress: string
      chain: {
        identifier: string
      }
    }
  }

type DropStoreCollectionData = FragmentOf<typeof dropStoreCollectionFragment>
type DropStoreDropData = FragmentOf<typeof dropStoreDropFragment>
export type DropStageData = NonNullable<
  ResultOf<typeof dropStoreDropFragment>
>["stages"][number] & {
  isEligible?: boolean
  eligiblePrice?: Price
  eligibleMaxTotalMintableByWallet?: number
  tokenDetailsMap?: Record<
    string,
    {
      price: Price
      maxTotalMintableByWallet: number
      // Eligibility overrides for this token
      isEligible?: boolean
      eligiblePrice?: Price
      eligibleMaxTotalMintableByWallet?: number
    }
  >
}

type DropState = {
  activeStageIndex: number
  dropStart: Date | null
  is1155Drop?: boolean
  status: DropStatus | null
  collectionSlug: string
  collectionName: string
  totalSupply: number | null
  maxSupply: number | null
  totalSupplyPerToken?: Record<string, number> | null
  maxSupplyPerToken?: Record<string, number> | null
  // Map of token options where the key is the group name
  tokenOptions?: Record<
    string,
    {
      groupValue: string
      tokenId: number
    }[]
  > | null
  stages: DropStageData[]
  currentStage: DropStageData | null

  // Eligibility state
  isEligibleForCurrentStage?: boolean
  maxMints?: number
  quantityMinted: number
  isLoadingEligibility: boolean
  eligibleStageIndices: number[]
  eligiblePrice:
    | (FragmentOf<typeof TokenPriceFragment> &
        FragmentOf<typeof UsdPriceFragment>)
    | null
  refetchEligibilityFn: (() => void) | null

  // UI state
  isReminderModalOpen: boolean
  showBottomBar: boolean
}

type EligibilityStageData = {
  stageIndex: number
  stageType: string
  isEligible?: boolean
  eligiblePrice?: Price
  eligibleMaxTotalMintableByWallet?: number
  eligibleMaxTotalMintableByWalletPerToken?: number
  fromTokenId?: number
  toTokenId?: number
}

export type DropStore = DropState & {
  setIsReminderModalOpen: (isOpen: boolean) => void
  setShowBottomBar: (visible: boolean) => void
  setTotalSupply: (totalSupply: number) => void
  updateTotalSupplyPerToken: (
    totalSupplyPerToken: {
      tokenId: string
      totalSupply: number
    }[],
  ) => void
  setQuantityMinted: (quantityMinted: number) => void
  setEligibilityData: (data: {
    isEligibleForCurrentStage?: boolean
    maxMints?: number
    eligibleStageIndices: number[]
    eligiblePrice:
      | (FragmentOf<typeof TokenPriceFragment> &
          FragmentOf<typeof UsdPriceFragment>)
      | null
  }) => void
  setIsLoadingEligibility: (isLoading: boolean) => void
  setRefetchEligibility: (refetchFn: () => void) => void
  refetchEligibility: () => void
  updateStageEligibility: (stageData: EligibilityStageData[]) => void

  // Helper methods for per-token eligibility (ERC1155)
  getTokenEligiblePrice: (tokenId: string) => Price | null
  getTokenMaxMints: (tokenId: string) => number | undefined
  isTokenEligible: (tokenId: string) => boolean | undefined

  syncActiveStageAndStatus: () => void
}

function createTokenOptionsMap(
  tokenMintDetails: {
    tokenId: number
    groupName: string
    groupValue: string
  }[],
): Record<string, { groupValue: string; tokenId: number }[]> {
  return tokenMintDetails.reduce<
    Record<string, { groupValue: string; tokenId: number }[]>
  >((acc, token) => {
    const { groupName, groupValue, tokenId } = token

    if (!acc[groupName]) {
      acc[groupName] = []
    }

    acc[groupName].push({
      groupValue,
      tokenId: Number(tokenId),
    })

    return acc
  }, {})
}

const initialDropState: DropState = {
  activeStageIndex: -1,
  dropStart: null,
  status: null,
  collectionSlug: "",
  collectionName: "",
  totalSupply: null,
  totalSupplyPerToken: null,
  maxSupply: null,
  maxSupplyPerToken: null,
  tokenOptions: null,
  stages: [],
  currentStage: null,

  // Eligibility defaults
  isEligibleForCurrentStage: undefined,
  maxMints: undefined,
  quantityMinted: 0,
  isLoadingEligibility: false,
  eligibleStageIndices: [],
  eligiblePrice: null,
  refetchEligibilityFn: null,

  // UI state
  isReminderModalOpen: false,
  showBottomBar: false,
}

const getStoreDataFromDrop = (
  collection: ResultOf<typeof dropStoreCollectionFragment>,
  drop: ResultOf<typeof dropStoreDropFragment>,
) => {
  const is1155Drop = drop.__typename === "Erc1155SeaDropV2"
  const { activeStageIndex } = findActiveDropStageIndex(collection)

  return {
    activeStageIndex,
    dropStart: drop.stages[0]?.startTime
      ? new Date(drop.stages[0].startTime)
      : null,
    status: getDropStatus(collection),
    is1155Drop,

    // for 1155s total supply and max supply are the sum of all token supply values
    totalSupply:
      drop.__typename === "Erc1155SeaDropV2"
        ? drop.tokenSupply.reduce((acc, token) => {
            return acc + Number(token.totalSupply)
          }, 0)
        : Number(drop.totalSupply),
    totalSupplyPerToken:
      drop.__typename === "Erc1155SeaDropV2"
        ? Object.fromEntries(
            drop.tokenSupply.map(token => [
              token.tokenId,
              Number(token.totalSupply),
            ]),
          )
        : null,
    maxSupply:
      drop.__typename === "Erc1155SeaDropV2"
        ? drop.tokenSupply.reduce((acc, token) => {
            return acc + Number(token.maxSupply)
          }, 0)
        : Number(drop.maxSupply),
    maxSupplyPerToken:
      drop.__typename === "Erc1155SeaDropV2"
        ? Object.fromEntries(
            drop.tokenSupply.map(token => [
              token.tokenId,
              Number(token.maxSupply),
            ]),
          )
        : null,
    tokenOptions:
      drop.__typename === "Erc1155SeaDropV2"
        ? createTokenOptionsMap(drop.tokenMintDetails)
        : null,

    // for Erc1155SeaDropV2, the stages array can contain multiple entries for a single stage, so we need to
    // merge stages with matching stageIndex
    // for Erc721SeaDropV1, we can just pass through stages
    stages: is1155Drop
      ? drop.stages.reduce<DropStageData[]>((acc, stage) => {
          const stageIndex = stage.stageIndex
          const existingStage = acc.find(s => s.stageIndex === stageIndex)

          // Generate tokenDetailsMap for this stage
          const tokenDetailsMap: Record<
            string,
            {
              price: Price
              maxTotalMintableByWallet: number
            }
          > = {}

          // Typeguard for Erc1155SeaDropV2Stage
          if (stage.__typename === "Erc1155SeaDropV2Stage") {
            const fromTokenId = Number(stage.fromTokenId)
            const toTokenId = Number(stage.toTokenId)

            for (let tokenId = fromTokenId; tokenId <= toTokenId; tokenId++) {
              tokenDetailsMap[tokenId.toString()] = {
                price: stage.price,
                maxTotalMintableByWallet:
                  stage.maxTotalMintableByWalletPerToken || 0,
              }
            }
          }

          if (existingStage) {
            // Merge the stage properties and update tokenDetailsMap
            Object.assign(existingStage, stage)
            Object.assign(existingStage.tokenDetailsMap || {}, tokenDetailsMap)
          } else {
            acc.push({ ...stage, tokenDetailsMap })
          }

          return acc
        }, [])
      : drop.stages,
  }
}

export const createDropStore = (
  collectionKey?: DropStoreCollectionData,
  dropKey?: DropStoreDropData,
) => {
  const collection = readFragment(dropStoreCollectionFragment, collectionKey)
  const drop = readFragment(dropStoreDropFragment, dropKey)
  let initialState = initialDropState

  if (drop && collection) {
    initialState = {
      ...initialDropState,
      collectionSlug: collection.slug,
      collectionName: collection.name,

      ...getStoreDataFromDrop(collection, drop),
    }
  }

  return create<DropStore>()(
    mutative((set, get) => ({
      ...initialState,

      setIsReminderModalOpen: isOpen => {
        set(state => {
          state.isReminderModalOpen = isOpen
        })
      },

      setShowBottomBar: visible => {
        set(state => {
          state.showBottomBar = visible
        })
      },

      setTotalSupply: totalSupply => {
        set(state => {
          state.totalSupply = totalSupply
        })
      },

      setQuantityMinted: quantityMinted => {
        set(state => {
          state.quantityMinted = quantityMinted
        })
      },

      setEligibilityData: data => {
        set(state => {
          state.isEligibleForCurrentStage = data.isEligibleForCurrentStage
          state.maxMints = data.maxMints
          state.eligibleStageIndices = data.eligibleStageIndices
          state.eligiblePrice = data.eligiblePrice
        })
      },

      setIsLoadingEligibility: isLoading => {
        set(state => {
          state.isLoadingEligibility = isLoading
        })
      },

      setRefetchEligibility: refetchFn => {
        set(state => {
          state.refetchEligibilityFn = refetchFn
        })
      },

      refetchEligibility: () => {
        get().refetchEligibilityFn?.()
      },

      updateStageEligibility: (
        eligibilityStageData: EligibilityStageData[],
      ) => {
        set(state => {
          state.stages.forEach(stage => {
            const eligibilityData = eligibilityStageData.find(
              data => data.stageIndex === stage.stageIndex,
            )

            if (eligibilityData) {
              stage.isEligible = eligibilityData.isEligible
              stage.eligiblePrice = eligibilityData.eligiblePrice
              stage.eligibleMaxTotalMintableByWallet =
                eligibilityData.eligibleMaxTotalMintableByWallet ?? 0

              if (stage.tokenDetailsMap) {
                Object.keys(stage.tokenDetailsMap).forEach(tokenId => {
                  const tokenIdNum = Number(tokenId)
                  const fromTokenId = eligibilityData.fromTokenId ?? 0
                  const toTokenId = eligibilityData.toTokenId ?? 0

                  // Check if this token is within the stage's token range
                  if (tokenIdNum >= fromTokenId && tokenIdNum <= toTokenId) {
                    const tokenDetails = stage.tokenDetailsMap?.[tokenId]
                    if (tokenDetails) {
                      tokenDetails.isEligible = eligibilityData.isEligible
                      tokenDetails.eligiblePrice = eligibilityData.eligiblePrice
                      tokenDetails.eligibleMaxTotalMintableByWallet =
                        eligibilityData.eligibleMaxTotalMintableByWalletPerToken
                    }
                  }
                })
              }
            }
          })
        })
      },

      // Helper methods for per-token eligibility (ERC1155)
      getTokenEligiblePrice: (tokenId: string) => {
        const state = get()
        if (!state.is1155Drop) {
          return null
        }

        const activeStage = state.stages.find(
          stage => stage.stageIndex === state.activeStageIndex,
        )

        if (!activeStage?.tokenDetailsMap?.[tokenId]) {
          return null
        }

        const tokenDetails = activeStage.tokenDetailsMap[tokenId]
        return tokenDetails.eligiblePrice ?? null
      },

      getTokenMaxMints: (tokenId: string) => {
        const state = get()
        if (!state.is1155Drop) {
          return undefined
        }

        const activeStage = state.stages.find(
          stage => stage.stageIndex === state.activeStageIndex,
        )

        if (!activeStage?.tokenDetailsMap?.[tokenId]) {
          return undefined
        }

        const tokenDetails = activeStage.tokenDetailsMap[tokenId]
        const maxMints =
          tokenDetails.eligibleMaxTotalMintableByWallet ??
          tokenDetails.maxTotalMintableByWallet

        return maxMints - state.quantityMinted
      },

      isTokenEligible: (tokenId: string) => {
        const state = get()
        if (!state.is1155Drop) {
          return undefined
        }

        const activeStage = state.stages.find(
          stage => stage.stageIndex === state.activeStageIndex,
        )

        if (!activeStage?.tokenDetailsMap?.[tokenId]) {
          return undefined
        }

        const tokenDetails = activeStage.tokenDetailsMap[tokenId]
        return (
          tokenDetails.isEligible ?? activeStage.stageType === "PUBLIC_SALE"
        )
      },

      syncActiveStageAndStatus: () => {
        set(state => {
          if (collection) {
            const { activeStageIndex } = findActiveDropStageIndex(collection)
            state.activeStageIndex = activeStageIndex
            state.status = getDropStatus(collection)
          }
        })
      },

      updateTotalSupplyPerToken: (
        tokenSupply: { tokenId: string; totalSupply: number }[],
      ) => {
        set(state => {
          state.totalSupplyPerToken = {
            ...(state.totalSupplyPerToken || {}),
            ...Object.fromEntries(
              tokenSupply.map(({ tokenId, totalSupply }) => [
                tokenId,
                Number(totalSupply),
              ]),
            ),
          }
        })
      },
    })),
  )
}
